/**
 * ConnectedGrowth -- wires URL params and Apollo queries to the library's
 * pure GrowthRenderer component.
 *
 * This follows the "connected wrapper" pattern established by ConnectedTreemap.
 * Growth uses the dedicated CPY_GROWTH_QUERY rather than the generic
 * determineEndpointFacet helper.
 *
 * Data flow:
 *   URL params (usePageQueryParams)
 *     -> Apollo query (CPY_GROWTH_QUERY)
 *       -> metadata tree + lookups
 *         -> derived values (filteredData, totalValue, findInVizOptions)
 *           -> GrowthRenderer (pure, from @atlas-int/viz)
 *             -> Scatterplot | ProductsTable (children, based on route)
 */

import React, { useCallback, useMemo, useState, memo } from "react";
import { useQuery } from "@apollo/client/react";
import { index, sum } from "d3";
import { useIsFeasibilityTable } from "../../routing/typedRoutes";
import { useFeasibilityParams } from "../paramSchemas";
import { useLocationLookups, PRODUCT_CLASS_METADATA_KEY } from "@atlas-int/data";
import { useHiddenCategories } from "@atlas-int/viz/treemap";
import { createProductHierarchyTree } from "@atlas-int/data";
import { CPY_GROWTH_QUERY } from "@atlas-int/data";
import { getCategoriesForChartLegend, getColorMap } from "@atlas-int/viz";
import { getClassificationServicesCategory, ProductClass, ServicesClass, ColorBy, LegendLabels } from "@atlas-int/data";

const hs22ServicesCategory = getClassificationServicesCategory(ProductClass.HS22Products);
import { OpportunitySizeOption, Scatterplot, ProductsTable } from "@atlas-int/viz/growth";
import { LegendRow, GraphNotice, GraphNoticeType, VisualizationContent } from "@atlas-int/viz/shared";
import { VisualizationContentContainer } from "../VizGrid";
import { CategoryLabels } from "@atlas-int/viz/shared";
import type { CategoryDatum } from "@atlas-int/viz/shared";
import type { FindInVizOption } from "@atlas-int/controls";
import type { Cpy_GrowthQuery } from "@atlas-int/data/graphql";
import type { HierarchyNode } from "d3-hierarchy";
import type { StratifyProductInput, ProductMetadatum } from "@atlas-int/data/types";
import VisualizationLayout from "../components/VisualizationLayout";

/** Narrow CPY row as returned by the growth query. */
type GrowthCPY = NonNullable<
  NonNullable<Cpy_GrowthQuery["countryProductYear"]>[number]
>;

/** Narrow PY row as returned by the growth query. */
type GrowthPY = NonNullable<
  NonNullable<Cpy_GrowthQuery["productYear"]>[number]
>;

/** Narrow CY row as returned by the growth query. */
type GrowthCY = NonNullable<
  NonNullable<Cpy_GrowthQuery["countryYear"]>[number]
>;

/** Narrow threshold row as returned by the growth query. */
type GrowthCYT = NonNullable<
  NonNullable<Cpy_GrowthQuery["countryYearThresholds"]>[number]
>;

/**
 * ConnectedGrowth reads all settings from URL query params, fetches data
 * via Apollo (CPY_GROWTH_QUERY), transforms it, and renders via GrowthRenderer.
 */
const ConnectedGrowth: React.FC = () => {
  // === URL PARAMS ===
  const [
    {
      productClass,
      productLevel,
      view,
      exporter,
      year,
      hideExports,
      yAxis,
      sizing,
    },
    setQuery,
  ] = useFeasibilityParams();

  const showTable = useIsFeasibilityTable();

  // Feasibility is country-only; Undeclared (country-999) and Other
  // (group-997) have no feasibility analysis — show a notice instead.
  const isNonGeographicEntity =
    exporter === "country-999" || exporter === "group-997";

  // === STATE ===
  const { hiddenCategories, setHiddenCategories } = useHiddenCategories(
    view,
    ColorBy.Sector,
  );
  const [highlightedItem, setHighlightedItem] = useState<string | undefined>();

  // === DATA FETCHING ===
  const exporterNumericId = (() => {
    const raw = exporter?.split("-")[1];
    if (raw === undefined) return undefined;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? undefined : n;
  })();

  const { loading, previousData, data } = useQuery<Cpy_GrowthQuery>(
    CPY_GROWTH_QUERY,
    {
      variables: {
        productClass,
        servicesClass: ServicesClass.unilateral,
        countryId: exporterNumericId,
        productLevel,
        yearMin: year,
        yearMax: year,
        hs12: productClass === "HS12",
        hs22: productClass === "HS22",
        hs92: productClass === "HS92",
        sitc: productClass === "SITC",
      },
    },
  );

  const displayData = data || previousData;

  // === NARROW QUERY RESULTS ===
  const countryProductYear: GrowthCPY[] = useMemo(
    () =>
      displayData?.countryProductYear?.filter(
        (d): d is GrowthCPY => d != null,
      ) ?? [],
    [displayData?.countryProductYear],
  );

  const productYear: GrowthPY[] = useMemo(
    () =>
      displayData?.productYear?.filter((d): d is GrowthPY => d != null) ?? [],
    [displayData?.productYear],
  );

  const countryData: GrowthCY | undefined = useMemo(
    () => displayData?.countryYear?.find((d): d is GrowthCY => d != null),
    [displayData?.countryYear],
  );

  const countryYearThresholds: GrowthCYT[] = useMemo(
    () =>
      displayData?.countryYearThresholds?.filter(
        (d): d is GrowthCYT => d != null,
      ) ?? [],
    [displayData?.countryYearThresholds],
  );

  // === LOOKUPS ===
  const locationLookupsState = useLocationLookups();
  const locationLookups = locationLookupsState.status === "success" ? locationLookupsState.data : undefined;
  const countryLookup = locationLookups?.byId || new Map();

  const metaDataTree = useMemo(() => {
    if (!displayData) {
      return createProductHierarchyTree([]);
    }
    const selectedMetadataKey =
      PRODUCT_CLASS_METADATA_KEY[productClass] as keyof Cpy_GrowthQuery;
    const metaData = (displayData[selectedMetadataKey] ??
      []) as ProductMetadatum[];
    return createProductHierarchyTree(metaData);
  }, [displayData, productClass]);

  type MetaDataNode = HierarchyNode<StratifyProductInput>;
  const metaDataLookup: Map<string, MetaDataNode> = useMemo(
    () =>
      index(
        metaDataTree.descendants() as MetaDataNode[],
        (d) => d.id!,
      ) as Map<string, MetaDataNode>,
    [metaDataTree],
  );

  const totalExportLookup: Map<string, GrowthPY> = useMemo(
    () =>
      index(productYear, (d) => d.productId as string) as Map<
        string,
        GrowthPY
      >,
    [productYear],
  );

  const colorMap = useMemo(
    () => getColorMap({ view, productClass }),
    [view, productClass],
  );

  // === DERIVED VALUES ===
  const filteredData = useMemo(() => {
    return countryProductYear.filter((d) => {
      if (d.year !== year) return false;

      if (!d.productId) return false;
      const productData = metaDataLookup.get(d.productId)?.data;
      if (!productData || !("productLevel" in productData)) return false;

      const topParentId = productData.topParent?.productId;
      if (productClass === "HS22" && topParentId === hs22ServicesCategory) {
        return false;
      }

      const showFeasibility =
        productClass === "HS22" ? true : productData.showFeasibility;
      if (!showFeasibility) return false;

      if (hideExports === "On" && !(Number(d.exportRca) < 1)) return false;

      const distance = Number(d.distance);
      if (!Number.isFinite(distance)) return false;

      const exportRca = Number(d.exportRca);
      if (!Number.isFinite(exportRca)) return false;

      const productYearDatum = totalExportLookup.get(d.productId!);
      if (!productYearDatum) return false;

      const yValue =
        yAxis === "cog" ? Number(d.cog) : Number(productYearDatum?.pci);
      if (!Number.isFinite(yValue)) return false;

      if (sizing === OpportunitySizeOption.WorldTrade) {
        const exportValue = Number(productYearDatum?.exportValue);
        if (!Number.isFinite(exportValue)) return false;
      }

      return !hiddenCategories.includes(topParentId);
    });
  }, [
    countryProductYear,
    year,
    metaDataLookup,
    hiddenCategories,
    productClass,
    hideExports,
    totalExportLookup,
    yAxis,
    sizing,
  ]);

  const totalValue = useMemo(() => {
    const value = sum(filteredData, (d) => d.exportValue || 0);
    return value > 0 ? value : undefined;
  }, [filteredData]);

  // Query completed but there's nothing plottable — either no rows at all,
  // or rows exist with null distance/RCA (e.g., South Sudan pre-2011). Show
  // the "no trade data reported" notice rather than an empty chart.
  const hasNoData = useMemo(() => {
    if (loading || !displayData) return false;
    return !countryProductYear.some(
      (d) =>
        d.year === year && d.distance != null && d.exportRca != null,
    );
  }, [loading, displayData, countryProductYear, year]);

  const findInVizOptions: FindInVizOption[] = useMemo(() => {
    return filteredData
      .map((d) =>
        d.productId ? metaDataLookup.get(d.productId) : undefined,
      )
      .filter((d): d is MetaDataNode => !!d?.data && "productLevel" in d.data)
      .map((d) => {
        const data = d.data as ProductMetadatum;
        return {
          id: data.productId,
          label: data.code
            ? `${data.nameShortEn} (${data.code} ${productClass})`
            : data.nameShortEn,
        };
      });
  }, [filteredData, metaDataLookup, productClass]);

  // === STABLE CALLBACKS ===
  const onYAxisChange = useCallback(
    (y: string) => setQuery({ yAxis: y }),
    [setQuery],
  );

  // === LEGEND ===
  const categoriesForLegend: CategoryDatum[] = useMemo(
    () =>
      getCategoriesForChartLegend({
        view,
        productClass,
      }).filter((d) => d.id.split("-")[2] !== "14"),
    [view, productClass],
  );

  // === RENDER ===
  // Child components use memo() for prop-based memoization — no need
  // for useMemo wrappers around JSX (which are error-prone due to
  // manual dependency lists that can go stale).
  return (
    <VisualizationLayout
      totalValue={totalValue}
      findInVizOptions={findInVizOptions}
      highlightedItem={highlightedItem}
      onHighlight={setHighlightedItem}
    >
      <VisualizationContentContainer>
        {isNonGeographicEntity ? (
          <GraphNotice graphNoticeType={GraphNoticeType.RegionNotDisplayed} />
        ) : (
          <VisualizationContent
            hasError={false}
            hasNoData={hasNoData}
            loading={false}
          >
            {!showTable && (
              <Scatterplot
                displayData={displayData}
                countryProductYear={countryProductYear}
                productYear={productYear}
                loading={loading}
                countryData={countryData}
                metaDataLookup={metaDataLookup}
                metaDataTree={metaDataTree}
                colorMap={colorMap}
                hiddenCategories={hiddenCategories}
                highlightedItem={highlightedItem}
                setHighlightedItem={setHighlightedItem}
                productClass={productClass}
                sizing={sizing}
                hideExports={hideExports}
                yAxis={yAxis || "cog"}
                onYAxisChange={onYAxisChange}
              />
            )}
            {showTable && (
              <ProductsTable
                displayData={displayData}
                countryProductYear={countryProductYear}
                productYear={productYear}
                loading={loading}
                countryData={countryData}
                metaDataLookup={metaDataLookup}
                metaDataTree={metaDataTree}
                colorMap={colorMap}
                countryLookup={countryLookup}
                hiddenCategories={hiddenCategories}
                countryYearThresholds={countryYearThresholds}
                productClass={productClass}
                year={year}
              />
            )}
          </VisualizationContent>
        )}
      </VisualizationContentContainer>
      <LegendRow>
        <CategoryLabels
          legendLabel={LegendLabels.Sector}
          categories={categoriesForLegend}
          allowToggle
          hiddenCategories={hiddenCategories}
          setHiddenCategories={setHiddenCategories}
          resetText="Show All Sectors"
          fullWidth={true}
        />
      </LegendRow>
    </VisualizationLayout>
  );
};

export default memo(ConnectedGrowth);
